import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  Plus, 
  RefreshCw, 
  ShoppingBag, 
  ExternalLink,
  MessageSquare,
  Truck,
  CheckCircle,
  Ban,
  Clock,
  Search,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { updateAdminOrderStatus } from "@/lib/store.functions";

type OrderStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  customer_name: string;
  customer_phone: string;
  items: OrderItem[];
  total_price: number;
  production_cost?: number;
  status: OrderStatus;
  tracking_code: string | null;
  created_at: string;
}

interface AdminOrdersProps {
  orders: Order[];
  isLoadingOrders: boolean;
  fetchOrders: () => void;
  products: any[];
}

export function AdminOrders({ orders, isLoadingOrders, fetchOrders, products }: AdminOrdersProps) {
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [isManualOrderOpen, setIsManualOrderOpen] = useState(false);
  const [manualOrder, setManualOrder] = useState({
    name: "",
    phone: "",
    total: "",
    cost: "",
    description: ""
  });

  const handleCreateManualOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualOrder.name || !manualOrder.total) {
      toast.error("Preencha nome e valor total.");
      return;
    }

    try {
      const { error } = await supabase
        .from('orders')
        .insert([{
          customer_name: manualOrder.name,
          customer_phone: manualOrder.phone.replace(/\D/g, ''),
          total_price: parseFloat(manualOrder.total),
          production_cost: parseFloat(manualOrder.cost || "0"),
          items: [{ 
            id: 'manual', 
            name: manualOrder.description || 'Pedido Manual', 
            quantity: 1, 
            price: parseFloat(manualOrder.total) 
          }],
          status: 'pending' as const
        }]);

      if (error) throw error;
      
      toast.success("Pedido manual criado!");
      setIsManualOrderOpen(false);
      setManualOrder({ name: "", phone: "", total: "", cost: "", description: "" });
      fetchOrders();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar pedido manual");
    }
  };

  const handleUpdateStatus = async (orderId: string, newStatus: OrderStatus, trackingCode?: string) => {
    setIsUpdating(orderId);
    try {
      const result = await updateAdminOrderStatus({
        orderId,
        status: newStatus,
        trackingCode
      });

      
      if (result.ok) {
        toast.success("Status atualizado e cliente notificado!");
        fetchOrders();
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Erro ao atualizar status");
    } finally {
      setIsUpdating(null);
    }
  };

  const getStatusBadge = (status: OrderStatus) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200">Pendente</Badge>;
      case 'processing': return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">Em Produção</Badge>;
      case 'shipped': return <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">Enviado</Badge>;
      case 'delivered': return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Entregue</Badge>;
      case 'cancelled': return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">Cancelado</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <ShoppingBag className="h-6 w-6 text-pink-500" />
          Gerenciamento de Pedidos
        </h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="rounded-xl border-pink-200 text-pink-600 hover:bg-pink-50 gap-2"
            onClick={() => setIsManualOrderOpen(!isManualOrderOpen)}
          >
            <Plus className="h-4 w-4" />
            Lançar Pedido Manual
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchOrders} disabled={isLoadingOrders}>
            <RefreshCw className={`h-4 w-4 ${isLoadingOrders ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {isManualOrderOpen && (
        <Card className="border-pink-100 bg-pink-50/30 overflow-hidden animate-in slide-in-from-top duration-300">
          <CardHeader className="pb-4">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Plus className="h-4 w-4 text-pink-500" />
              Novo Pedido Manual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateManualOrder} className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-[10px] uppercase font-bold text-gray-400">Nome do Cliente</Label>
                <Input 
                  placeholder="Ex: Maria Oliveira"
                  value={manualOrder.name}
                  onChange={e => setManualOrder({...manualOrder, name: e.target.value})}
                  className="rounded-xl h-10"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-[10px] uppercase font-bold text-gray-400">WhatsApp</Label>
                <Input 
                  placeholder="5511999999999"
                  value={manualOrder.phone}
                  onChange={e => setManualOrder({...manualOrder, phone: e.target.value})}
                  className="rounded-xl h-10"
                />
              </div>
              
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-[10px] uppercase font-bold text-gray-400">Selecionar Produto Cadastrado</Label>
                <Select onValueChange={(val) => {
                  const prod = products.find(p => p.id === val);
                  if (prod) {
                    setManualOrder({
                      ...manualOrder,
                      total: prod.price.toString(),
                      description: prod.name
                    });
                  }
                }}>
                  <SelectTrigger className="rounded-xl h-10 bg-white">
                    <SelectValue placeholder="Escolha um produto da sua lista..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} - {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(p.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase font-bold text-gray-400">Valor Total (R$)</Label>
                <Input 
                  type="number"
                  placeholder="0.00"
                  value={manualOrder.total}
                  onChange={e => setManualOrder({...manualOrder, total: e.target.value})}
                  className="rounded-xl h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase font-bold text-gray-400">Custo (R$)</Label>
                <Input 
                  type="number"
                  placeholder="0.00"
                  value={manualOrder.cost}
                  onChange={e => setManualOrder({...manualOrder, cost: e.target.value})}
                  className="rounded-xl h-10"
                />
              </div>
              
              <div className="md:col-span-3">
                <Button type="submit" className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold rounded-xl h-10">
                  Criar Pedido Manual
                </Button>
              </div>
              <div className="md:col-span-1">
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="w-full text-gray-400 font-bold h-10"
                  onClick={() => setIsManualOrderOpen(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6">
        {isLoadingOrders ? (
          <div className="py-20 flex flex-col items-center justify-center text-gray-400">
            <Loader2 className="h-8 w-8 animate-spin mb-2" />
            <p className="text-sm">Carregando seus pedidos...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-gray-400 bg-white rounded-3xl border-2 border-dashed border-gray-100">
            <ShoppingBag className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">Nenhum pedido encontrado</p>
            <p className="text-sm">Os pedidos aparecerão aqui assim que seus clientes comprarem.</p>
          </div>
        ) : (
          orders.map((order) => (
            <Card key={order.id} className="border-none shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 rounded-3xl group">
              <div className={`h-1.5 w-full ${
                order.status === 'pending' ? 'bg-yellow-400' : 
                order.status === 'processing' ? 'bg-blue-400' : 
                order.status === 'shipped' ? 'bg-purple-400' : 
                order.status === 'delivered' ? 'bg-green-400' : 'bg-red-400'
              }`} />
              <CardContent className="p-0">
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-gray-50 rounded-2xl flex items-center justify-center shrink-0">
                        <ShoppingBag className="h-6 w-6 text-gray-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-black text-gray-900">{order.customer_name}</h3>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-xs text-gray-400 flex items-center gap-2 mt-1">
                          <Clock className="h-3 w-3" />
                          {new Date(order.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <div className="text-left md:text-right">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-1">Total do Pedido</p>
                      <p className="text-2xl font-black text-pink-500">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(order.total_price)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50/50 rounded-2xl p-4 mb-6">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Itens da Encomenda</p>
                    <div className="space-y-3">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-xl shadow-sm border border-gray-100">
                          <div className="flex items-center gap-3">
                            <span className="h-6 w-6 bg-pink-100 text-pink-600 rounded-lg flex items-center justify-center text-[10px] font-black">
                              {item.quantity}x
                            </span>
                            <span className="text-sm font-bold text-gray-700">{item.name}</span>
                          </div>
                          <span className="text-sm font-black text-gray-900">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.price * item.quantity)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="rounded-xl border-green-200 text-green-600 hover:bg-green-50 gap-2 h-9"
                        asChild
                      >
                        <a 
                          href={`https://wa.me/${order.customer_phone.replace(/\D/g, '')}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <MessageSquare className="h-4 w-4" />
                          WhatsApp
                        </a>
                      </Button>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {order.status === 'pending' && (
                        <Button 
                          size="sm" 
                          className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold h-9 gap-2"
                          onClick={() => handleUpdateStatus(order.id, 'processing')}
                          disabled={isUpdating === order.id}
                        >
                          {isUpdating === order.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                          Iniciar Produção
                        </Button>
                      )}

                      {order.status === 'processing' && (
                        <div className="flex gap-2">
                          <Input 
                            placeholder="Cód. Rastreio (opcional)" 
                            className="h-9 w-32 rounded-xl text-xs"
                            id={`tracking-${order.id}`}
                          />
                          <Button 
                            size="sm" 
                            className="bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-bold h-9 gap-2"
                            onClick={() => {
                              const input = document.getElementById(`tracking-${order.id}`) as HTMLInputElement;
                              handleUpdateStatus(order.id, 'shipped', input?.value);
                            }}
                            disabled={isUpdating === order.id}
                          >
                            {isUpdating === order.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                            Enviar
                          </Button>
                        </div>
                      )}

                      {order.status === 'shipped' && (
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold h-9 gap-2"
                          onClick={() => handleUpdateStatus(order.id, 'delivered')}
                          disabled={isUpdating === order.id}
                        >
                          {isUpdating === order.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                          Confirmar Entrega
                        </Button>
                      )}

                      {order.status !== 'delivered' && order.status !== 'cancelled' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl h-9 gap-2"
                          onClick={() => handleUpdateStatus(order.id, 'cancelled')}
                          disabled={isUpdating === order.id}
                        >
                          <Ban className="h-4 w-4" />
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
